import { debounce, isEmpty } from "lodash";
import moment from "moment";
import { RangeModeTypes } from "PFApp/booking/types";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef } from "react";

import { useCalendarContext } from "../context/calendar_context";
import Legend from "../legend";
import css from "./calendar.module.scss";
import { getModeDays, MODE_TO_DAYS, setTimeToMidnight } from "./calendar.utils";
import CalendarView from "./calendar_view";
import Controls from "./controls";
import ScrollBar from "./scroll_bar";

export const GANTT_MATCH_MONTHS_LIMIT = 6; // for performance
export const GANTT_MONTHS_LIMIT = 12; // for performance
export const MIN_DAY_WIDTH = 40;
export const MONTH_HEADER_HEIGHT = 40;
export const DAY_HEADER_HEIGHT = 40;
export const CONTROLS_HEIGHT = 49;
export const FILTERS_ROW_HEIGHT = 54;
export const BAR_HEIGHT = 40;
export const OVERBOOKINGS_HEIGHT = 6;
export const OVERBOOKINGS_TOP = 12;
export const ROW_PADDING = 5;
export const VERTICAL_BARS_MARGIN = 2;

const Calendar = ({
  offsetTop,
  scrollWrapperRef,
  startDate,
  endDate,
  rangeMode,
  onRangeModeChange,
  headerRef,
  widthScrolled,
  scrollToLeftOffset,
  handleCalendarViewScroll
}) => {
  const { calendarRef, dayWidth, days } = useCalendarContext();
  const { utc, formatDatabaseDateTime } = useDateFormatter();
  const todayScrollOffset = useRef();
  const prevDayWidth = useRef(dayWidth);

  useEffect(() => {
    if (!isEmpty(days)) {
      const todayDate = setTimeToMidnight(new Date());
      const { left = 0 } = days.find(({ date }) => date.getTime() === todayDate.getTime()) || {};
      todayScrollOffset.current = left;
      let daysFromStart;
      if (rangeMode.type === RangeModeTypes.Custom) {
        daysFromStart = moment.duration(utc(rangeMode.start).diff(startDate)).asDays();
      } else {
        daysFromStart = Math.round(widthScrolled / (prevDayWidth.current || dayWidth));
      }
      scrollToLeftOffset(daysFromStart * dayWidth);
      prevDayWidth.current = dayWidth;
    }
  }, [days]);

  const debouncedSetToday = debounce(() => {
    scrollToLeftOffset(todayScrollOffset.current);
  }, 200);

  useEffect(() => {
    debouncedSetToday();
  }, []);

  const range = useMemo(() => {
    const daysScrolled = Math.round(widthScrolled / dayWidth);
    return {
      start: formatDatabaseDateTime(utc(startDate).add(daysScrolled, "days")),
      end: formatDatabaseDateTime(utc(startDate).add(daysScrolled + getModeDays(rangeMode, utc) - 1, "days"))
    };
  }, [widthScrolled, dayWidth, rangeMode, startDate, formatDatabaseDateTime, utc]);

  const handleCustomRangeChange = ({ start, end }) => {
    const daysInRange = getModeDays({ type: RangeModeTypes.Custom, start, end }, utc);
    const type = Object.entries(MODE_TO_DAYS).find(([, value]) => value === daysInRange)?.[0];
    onRangeModeChange(type ? { type } : { type: RangeModeTypes.Custom, start, end });
  };

  const isTodayDisabled = Math.abs(widthScrolled - todayScrollOffset.current) <= 1;
  const isDirectionDisabled = (value) => {
    const { clientWidth, scrollWidth } = scrollWrapperRef.current || {};
    return !scrollWrapperRef.current
      ? true
      : value > 0
      ? widthScrolled + clientWidth >= scrollWidth
      : widthScrolled === 0;
  };

  /* eslint-disable jsx-a11y/no-static-element-interactions */
  return (
    <div className={css.container} ref={calendarRef}>
      <Controls
        range={range}
        rangeMode={rangeMode}
        onRangeModeChange={onRangeModeChange}
        isTodayDisabled={isTodayDisabled}
        onTodayClick={() => scrollToLeftOffset(todayScrollOffset.current, true)}
        isDirectionDisabled={isDirectionDisabled}
        onDirectionClick={(step) => scrollToLeftOffset(widthScrolled + step, true)}
      />
      <CalendarView
        headerRef={headerRef}
        ref={scrollWrapperRef}
        startDate={startDate}
        endDate={endDate}
        offsetTop={offsetTop}
        onScroll={handleCalendarViewScroll}
      />
      <div className={css.footer}>
        <Legend />
        <ScrollBar
          calendarStartDate={startDate}
          calendarEndDate={endDate}
          calendarRef={scrollWrapperRef}
          range={range}
          onRangeChange={handleCustomRangeChange}
          scrollToLeftOffset={(value) => scrollToLeftOffset(value, false, true)}
        />
      </div>
    </div>
  );
};
Calendar.displayName = "Calendar";

Calendar.propTypes = {
  offsetTop: PropTypes.number,
  scrollWrapperRef: PropTypes.shape({ current: PropTypes.object }),
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  rangeMode: PropTypes.object,
  onRangeModeChange: PropTypes.func,
  calendarWidth: PropTypes.number,
  headerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  widthScrolled: PropTypes.number,
  scrollToLeftOffset: PropTypes.func,
  handleCalendarViewScroll: PropTypes.func
};

export default Calendar;
